/**
 * BEEZFY HEADER CONTROLLER - Sistema Unificato
 * Gestione completa header sticky con tutti i controlli
 */

class BeezfyHeaderController {
  constructor() {
    // Elementi principali
    this.controller = document.querySelector('[data-header-controller]');
    this.header = this.controller ? this.controller.querySelector('.beezfy-header') : document.querySelector('.beezfy-header');
    this.announcement = this.controller ? this.controller.querySelector('[data-announcement-bar]') : document.querySelector('[data-announcement-bar]');
    this.mobileMenu = document.getElementById('mobile-menu-details');
    this.dropdowns = this.controller ? this.controller.querySelectorAll('[data-dropdown]') : document.querySelectorAll('[data-dropdown]');
    this.root = document.documentElement;
    this.sectionWrapper = null;
    this.sectionOuterWrapper = null;
    this.stickyWrapper = null;
    this.host = null;
    this.compactMeasureFrame = null;
    this.resizeObserver = null;
    this.dropdownDocumentListenerBound = false;
    this.handleDropdownDocumentClick = this.handleDropdownDocumentClick.bind(this);
    this.mobileMenuDocumentListenersBound = false;
    this.handleMobileMenuDocumentClick = this.handleMobileMenuDocumentClick.bind(this);
    this.handleMobileMenuKeydown = this.handleMobileMenuKeydown.bind(this);
    this.handleMobileMenuToggle = this.handleMobileMenuToggle.bind(this);
    this.searchListenersBound = false;
    this.handleSearchDocumentClick = this.handleSearchDocumentClick.bind(this);
    this.handleSearchDocumentKeydown = this.handleSearchDocumentKeydown.bind(this);

    // Configurazione sticky header
    this.headerStickyConfig = {
      enabled: false,
      behavior: 'always',
      threshold: 50,
      compactEnabled: false,
      compactThreshold: 200
    };

    // Configurazione sticky announcement
    this.announcementStickyConfig = {
      enabled: false,
      behavior: 'always',
      threshold: 0,
      hideOnScrollDown: false
    };

    // Stato runtime
    this.state = {
      header: { fixed: false, hidden: false, compact: false },
      announcement: { fixed: false, hidden: false, independent: false }
    };

    this.headerHeight = 0;
    this.headerNavHeight = 0;
    this.announcementHeight = 0;
    this.announcementInsideHeader = false;

    // Stato scroll
    this.lastScrollY = 0;
    this.scrollTicking = false;

    // Inizializza solo se l'header esiste
    if (this.controller && this.header) {
      this.init();
    }
  }

  init() {
    // Leggi configurazione dai data attributes
    this.readConfig();
    this.captureStructure();

    // Setup funzionalità
    this.setupMobileMenu();
    this.setupDropdowns();
    this.setupSearchToggle();
    this.setupKeyboardNavigation();

    // Setup sticky e scroll
    this.updateHeights();
    this.setupScrollHandling();
    this.setupResizeObserver();

    // Stato iniziale
    this.updateStickyState(window.scrollY, 0, true);

    // Aggiorna nuovamente dopo caricamento completo (fonts, immagini)
    window.addEventListener('load', () => {
      this.updateHeights();
      this.updateStickyState(window.scrollY, 0, true);
    }, { once: true });
  }

  captureStructure() {
    if (!this.header) {
      this.sectionWrapper = null;
      this.sectionOuterWrapper = null;
      this.stickyWrapper = null;
      this.host = this.controller || null;
      this.announcementInsideHeader = false;
      return;
    }

    this.host = this.controller || this.header.parentElement || null;
    this.sectionWrapper = this.header.closest('#shopify-section-header, .shopify-section-header, .shopify-section') || null;
    this.sectionOuterWrapper = this.sectionWrapper ? this.sectionWrapper.closest('.shopify-section') : null;
    this.stickyWrapper = this.header.closest('sticky-header') || null;

    this.announcementInsideHeader = this.announcement ? this.header.contains(this.announcement) : false;
  }

  readConfig() {
    if (!this.controller) return;

    const dataset = this.controller.dataset;

    const stickyThreshold = Number.parseInt(dataset.stickyThreshold, 10);
    const compactThreshold = Number.parseInt(dataset.compactThreshold, 10);
    const announcementThreshold = Number.parseInt(dataset.announcementStickyThreshold, 10);

    this.headerStickyConfig = {
      enabled: dataset.stickyEnabled === 'true',
      behavior: dataset.stickyBehavior || 'always',
      threshold: Number.isNaN(stickyThreshold) ? 50 : stickyThreshold,
      compactEnabled: dataset.compactEnabled === 'true',
      compactThreshold: Number.isNaN(compactThreshold) ? 200 : compactThreshold
    };

    this.announcementStickyConfig = {
      enabled: dataset.announcementStickyEnabled === 'true',
      behavior: dataset.announcementStickyBehavior || 'always',
      threshold: Number.isNaN(announcementThreshold) ? 0 : announcementThreshold,
      hideOnScrollDown: dataset.announcementHideOnDown === 'true'
    };

    if (document.body) {
      document.body.classList.toggle('beezfy-announcement-hide-enabled', this.announcementStickyConfig.hideOnScrollDown);
      document.body.classList.toggle('beezfy-announcement-sticky-enabled', this.announcementStickyConfig.enabled);
      document.body.dataset.beezfyAnnouncementBehavior = this.announcementStickyConfig.behavior;
      document.body.dataset.beezfyAnnouncementThreshold = String(this.announcementStickyConfig.threshold);
    }
  }

  updateHeights() {
    this.measureDimensions();
    this.applyGlobalOffsets(this.state.header, this.state.announcement);
  }

  setupScrollHandling() {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - this.lastScrollY;

      // Aggiorna classi di direzione
      if (currentY > this.lastScrollY) {
        document.body.classList.add('scroll-down');
        document.body.classList.remove('scroll-up');
      } else {
        document.body.classList.add('scroll-up');
        document.body.classList.remove('scroll-down');
      }

      // Aggiorna stato sticky
      this.updateStickyState(currentY, delta, false);

      // Gestione transparent header
      if (this.header && this.header.classList.contains('is-transparent')) {
        this.header.classList.toggle('scrolled', currentY > 10);
      }

      this.lastScrollY = currentY;
      this.scrollTicking = false;
    };

    // Throttle scroll events
    window.addEventListener('scroll', () => {
      if (!this.scrollTicking) {
        window.requestAnimationFrame(handleScroll);
        this.scrollTicking = true;
      }
    }, { passive: true });
  }

  updateStickyState(currentY, delta, force = false) {
    if (!this.controller) return;

    const headerState = this.updateHeaderSticky(currentY, delta, force);
    const announcementState = this.updateAnnouncementSticky(currentY, delta, force, headerState);

    this.applyGlobalOffsets(headerState, announcementState);
  }

  updateHeaderSticky(currentY, delta, force) {
    const defaultState = { fixed: false, hidden: false, compact: false };

    if (!this.controller || !this.header) {
      this.state.header = defaultState;
      return this.state.header;
    }

    const { enabled, behavior, threshold, compactEnabled, compactThreshold } = this.headerStickyConfig;

    if (!enabled) {
      this.controller.classList.remove('is-fixed', 'is-hidden', 'is-compact');
      this.state.header = defaultState;
      return this.state.header;
    }

    const aboveThreshold = currentY > threshold;

    let shouldFix = false;
    let shouldHide = this.state.header.hidden;

    switch (behavior) {
      case 'always':
        shouldFix = true;
        shouldHide = false;
        break;

      case 'after_scroll':
        shouldFix = aboveThreshold;
        if (!shouldFix) {
          shouldHide = false;
        }
        break;

      case 'on_scroll_up':
        shouldFix = aboveThreshold;
        if (!shouldFix) {
          shouldHide = false;
        } else if (!force) {
          if (delta > 2) {
            shouldHide = true;
          } else if (delta < -2) {
            shouldHide = false;
          }
        } else {
          shouldHide = false;
        }
        break;

      case 'smart':
        shouldFix = aboveThreshold;
        if (!shouldFix) {
          shouldHide = false;
        } else if (!force && Math.abs(delta) > 2) {
          shouldHide = delta > 0;
        } else if (force) {
          shouldHide = false;
        }
        break;

      default:
        shouldFix = true;
        shouldHide = false;
    }

    if (!shouldFix) {
      shouldHide = false;
    }

    let shouldCompact = false;
    if (compactEnabled && shouldFix) {
      if (behavior === 'always') {
        shouldCompact = true;
      } else {
        shouldCompact = currentY > compactThreshold;
      }
    }

    this.controller.classList.toggle('is-fixed', shouldFix);
    this.controller.classList.toggle('is-hidden', shouldHide);
    if (shouldCompact) {
      this.controller.classList.add('is-compact');
    } else {
      this.controller.classList.remove('is-compact');
    }

    const prevState = this.state.header;
    const nextState = {
      fixed: shouldFix,
      hidden: shouldHide,
      compact: shouldCompact
    };

    this.state.header = nextState;

    if (!prevState || prevState.fixed !== nextState.fixed || prevState.hidden !== nextState.hidden || prevState.compact !== nextState.compact) {
      this.queueMeasurement();
    }

    return this.state.header;
  }

  updateAnnouncementSticky(currentY, delta, force, headerState = this.state.header) {
    const defaultState = { fixed: false, hidden: false, independent: false };

    if (!this.controller || !this.announcement) {
      this.state.announcement = defaultState;
      return this.state.announcement;
    }

    const { enabled, behavior, threshold, hideOnScrollDown } = this.announcementStickyConfig;

    if (!enabled) {
      this.controller.classList.remove('announcement-is-fixed', 'announcement-is-hidden');
      this.announcement.classList.remove('is-fixed', 'is-hidden');
      this.state.announcement = defaultState;
      return this.state.announcement;
    }

    const aboveThreshold = currentY > threshold;

    let shouldFix = false;
    let shouldHide = hideOnScrollDown ? this.state.announcement.hidden : false;

    switch (behavior) {
      case 'always':
        shouldFix = true;
        if (hideOnScrollDown && !force) {
          if (delta > 2) {
            shouldHide = true;
          } else if (delta < -2) {
            shouldHide = false;
          }
        } else {
          shouldHide = false;
        }
        break;

      case 'after_scroll':
        shouldFix = aboveThreshold;
        if (!shouldFix) {
          shouldHide = false;
        } else if (hideOnScrollDown && !force) {
          if (delta > 2) {
            shouldHide = true;
          } else if (delta < -2) {
            shouldHide = false;
          }
        }
        break;

      case 'on_scroll_up':
        shouldFix = aboveThreshold;
        if (!shouldFix) {
          shouldHide = false;
        } else if (hideOnScrollDown && !force) {
          if (delta > 2) {
            shouldHide = true;
          } else if (delta < -2) {
            shouldHide = false;
          }
        } else {
          shouldHide = false;
        }
        break;

      case 'smart':
        shouldFix = aboveThreshold;
        if (!shouldFix) {
          shouldHide = false;
        } else if (hideOnScrollDown && !force && Math.abs(delta) > 2) {
          shouldHide = delta > 0;
        } else if (force) {
          shouldHide = false;
        }
        break;

      default:
        shouldFix = true;
        shouldHide = false;
    }

    if (!shouldFix) {
      shouldHide = false;
    }

    const independentFix = shouldFix && !headerState.fixed;
    const effectiveFixed = headerState.fixed || independentFix;
    const applyFixedClasses = independentFix;

    this.controller.classList.toggle('announcement-is-fixed', applyFixedClasses);
    this.controller.classList.toggle('announcement-is-hidden', shouldHide);
    this.announcement.classList.toggle('is-fixed', applyFixedClasses);
    this.announcement.classList.toggle('is-hidden', shouldHide);

    const prevAnnouncement = this.state.announcement;
    const nextAnnouncement = {
      fixed: effectiveFixed,
      hidden: shouldHide,
      independent: independentFix
    };

    this.state.announcement = nextAnnouncement;

    if (!prevAnnouncement || prevAnnouncement.fixed !== nextAnnouncement.fixed || prevAnnouncement.hidden !== nextAnnouncement.hidden || prevAnnouncement.independent !== nextAnnouncement.independent) {
      this.queueMeasurement();
    }

    return this.state.announcement;
  }

  applyGlobalOffsets(headerState, announcementState) {
    if (!document.body) return;

    this.measureDimensions();

    const headerVisible = headerState.fixed && !headerState.hidden;
    const announcementVisible = announcementState.fixed && !announcementState.hidden;
    const announcementWithinHeader = this.announcementInsideHeader && headerVisible && !announcementState.independent && !announcementState.hidden;

    const headerOffset = headerVisible ? this.headerNavHeight : 0;
    const announcementOffset = (announcementVisible || announcementWithinHeader) ? this.announcementHeight : 0;
    const totalOffset = headerOffset + announcementOffset;
    const headerTopOffset = (!announcementWithinHeader && announcementVisible) ? this.announcementHeight : 0;
    const announcementFixedHidden = announcementState.fixed && announcementState.hidden;

    const effectiveAnnouncementHeight = announcementState.hidden ? 0 : this.announcementHeight;
    const effectiveTotalHeight = this.headerNavHeight + effectiveAnnouncementHeight;

    this.setCSSVar('--beezfy-announcement-height', `${effectiveAnnouncementHeight}px`);
    this.setCSSVar('--beezfy-total-header-height', `${effectiveTotalHeight}px`);
    this.setCSSVar('--total-header-height', `${effectiveTotalHeight}px`);

    document.body.classList.toggle('beezfy-header-fixed', headerVisible);
    document.body.classList.toggle('beezfy-header-hidden', headerState.fixed && headerState.hidden);
    document.body.classList.toggle('beezfy-announcement-fixed', announcementVisible);
    document.body.classList.toggle('beezfy-announcement-hidden', announcementFixedHidden);
    document.body.classList.toggle('beezfy-announcement-in-header', announcementWithinHeader);

    const announcementStateKey = announcementFixedHidden
      ? 'hidden'
      : announcementVisible
        ? 'fixed'
        : this.announcementStickyConfig.enabled
          ? 'enabled'
          : 'disabled';

    document.body.dataset.beezfyHeaderOffset = `${totalOffset}`;
    document.body.dataset.beezfyAnnouncementOffset = `${announcementOffset}`;
    document.body.dataset.beezfyAnnouncementState = announcementStateKey;

    const targetElements = [...new Set([
      this.host,
      this.sectionWrapper,
      this.sectionOuterWrapper,
      this.stickyWrapper
    ].filter(Boolean))];
    targetElements.forEach((el) => {
      el.classList.toggle('header-fixed-active', headerVisible);
      el.classList.toggle('header-hidden', headerState.fixed && headerState.hidden);
    });

    this.setCSSVar('--beezfy-header-offset', `${headerOffset}px`);
    this.setCSSVar('--beezfy-announcement-offset', `${announcementOffset}px`);
    this.setCSSVar('--beezfy-active-header-offset', `${totalOffset}px`);
    this.setCSSVar('--beezfy-header-top-offset', `${headerTopOffset}px`);

    if (this.root) {
      this.root.style.scrollPaddingTop = `${totalOffset}px`;
    }
  }

  setCSSVar(name, value) {
    if (this.root) {
      this.root.style.setProperty(name, value);
    }
  }

  measureDimensions() {
    if (!this.header) {
      this.headerHeight = 0;
      this.headerNavHeight = 0;
      this.announcementHeight = 0;
      return;
    }

    const announcementRect = this.announcement ? this.announcement.getBoundingClientRect() : null;
    let announcementHeight = announcementRect ? Math.max(0, Math.round(announcementRect.height)) : 0;
    if ((!announcementHeight || announcementHeight < 5) && this.announcement) {
      const content = this.announcement.querySelector('.beezfy-announcement-content');
      if (content) {
        const contentRect = content.getBoundingClientRect();
        announcementHeight = Math.max(announcementHeight, Math.round(contentRect.height));
      }
    }
    if (!announcementHeight && this.announcementHeight) {
      announcementHeight = this.announcementHeight;
    }

    const headerRect = this.header ? this.header.getBoundingClientRect() : null;
    const headerHeightRaw = headerRect ? Math.max(0, Math.round(headerRect.height)) : (this.header ? Math.round(this.header.offsetHeight) : 0);

    const insideHeader = this.announcement && this.header ? this.header.contains(this.announcement) : false;
    const navHeight = insideHeader ? Math.max(headerHeightRaw - announcementHeight, 0) : headerHeightRaw;
    const totalHeight = navHeight + announcementHeight;

    this.announcementInsideHeader = insideHeader;
    this.announcementHeight = announcementHeight;
    this.headerNavHeight = navHeight;
    this.headerHeight = headerHeightRaw;

    this.setCSSVar('--header-height', `${headerHeightRaw}px`);
    this.setCSSVar('--announcement-height', `${announcementHeight}px`);
    this.setCSSVar('--total-header-height', `${totalHeight}px`);
    this.setCSSVar('--beezfy-header-height', `${headerHeightRaw}px`);
    this.setCSSVar('--beezfy-header-nav-height', `${navHeight}px`);
    this.setCSSVar('--beezfy-announcement-height', `${announcementHeight}px`);
    this.setCSSVar('--beezfy-total-header-height', `${totalHeight}px`);

    if (document.body) {
      document.body.dataset.beezfyAnnouncementHeight = `${announcementHeight}`;
      document.body.dataset.beezfyHeaderHeight = `${headerHeightRaw}`;
      document.body.dataset.beezfyHeaderNavHeight = `${navHeight}`;
    }

    if (this.announcement) {
      if (announcementHeight) {
        this.announcement.style.height = `${announcementHeight}px`;
        this.announcement.style.minHeight = `${announcementHeight}px`;
      } else {
        this.announcement.style.height = '';
        this.announcement.style.minHeight = '';
      }
    }
  }

  queueMeasurement() {
    if (this.compactMeasureFrame) {
      cancelAnimationFrame(this.compactMeasureFrame);
    }

    this.compactMeasureFrame = requestAnimationFrame(() => {
      this.compactMeasureFrame = null;
      this.measureDimensions();
      this.applyGlobalOffsets(this.state.header, this.state.announcement);
    });
  }

  setupResizeObserver() {
    if (!('ResizeObserver' in window)) return;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updateHeights();
      this.updateStickyState(window.scrollY, 0, true);
    });

    if (this.header) this.resizeObserver.observe(this.header);
    if (this.announcement) this.resizeObserver.observe(this.announcement);
  }

  setupMobileMenu() {
    if (!this.mobileMenu) return;

    const closeButton = this.controller ? this.controller.querySelector('.beezfy-mobile-menu__close') : document.querySelector('.beezfy-mobile-menu__close');

    // Chiudi con bottone close
    if (closeButton && closeButton.dataset.mobileMenuBound !== 'true') {
      closeButton.addEventListener('click', () => {
        this.closeMobileMenu();
      });
      closeButton.dataset.mobileMenuBound = 'true';
    }

    // Chiudi cliccando fuori
    if (!this.mobileMenuDocumentListenersBound) {
      document.addEventListener('click', this.handleMobileMenuDocumentClick);
      document.addEventListener('keydown', this.handleMobileMenuKeydown);
      this.mobileMenuDocumentListenersBound = true;
    }

    // Chiudi con ESC
    if (!this.mobileMenu.dataset.toggleBound) {
      this.mobileMenu.addEventListener('toggle', this.handleMobileMenuToggle);
      this.mobileMenu.dataset.toggleBound = 'true';
    }
  }

  closeMobileMenu() {
    if (this.mobileMenu?.open) {
      this.mobileMenu.open = false;
    }
  }

  handleMobileMenuDocumentClick(e) {
    if (this.mobileMenu?.open && !this.mobileMenu.contains(e.target)) {
      this.closeMobileMenu();
    }
  }

  handleMobileMenuKeydown(e) {
    if (e.key === 'Escape' && this.mobileMenu?.open) {
      this.closeMobileMenu();
    }
  }

  handleMobileMenuToggle() {
    document.body.style.overflow = this.mobileMenu?.open ? 'hidden' : '';
  }

  setupDropdowns() {
    if (!this.dropdowns || this.dropdowns.length === 0) return;

    this.dropdowns.forEach(dropdown => {
      if (dropdown.dataset.dropdownBound === 'true') return;

      const trigger = dropdown.querySelector('[data-dropdown-trigger]');
      const content = dropdown.querySelector('[data-dropdown-content]');

      if (!trigger || !content) return;

      let timeout;

      // Mouse events
      dropdown.addEventListener('mouseenter', () => {
        clearTimeout(timeout);
        this.openDropdown(dropdown);
      });

      dropdown.addEventListener('mouseleave', () => {
        timeout = setTimeout(() => {
          this.closeDropdown(dropdown);
        }, 150);
      });

      // Keyboard events
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleDropdown(dropdown);
        } else if (e.key === 'Escape') {
          this.closeDropdown(dropdown);
          trigger.focus();
        }
      });

      dropdown.dataset.dropdownBound = 'true';
    });

    if (!this.dropdownDocumentListenerBound) {
      document.addEventListener('click', this.handleDropdownDocumentClick);
      this.dropdownDocumentListenerBound = true;
    }
  }

  handleDropdownDocumentClick(e) {
    if (!this.dropdowns) return;

    this.dropdowns.forEach(dropdown => {
      if (!dropdown.contains(e.target)) {
        this.closeDropdown(dropdown);
      }
    });
  }

  openDropdown(dropdown) {
    // Chiudi altri dropdown
    this.dropdowns.forEach(other => {
      if (other !== dropdown) {
        this.closeDropdown(other);
      }
    });

    dropdown.classList.add('open');
    dropdown.setAttribute('aria-expanded', 'true');
  }

  closeDropdown(dropdown) {
    dropdown.classList.remove('open');
    dropdown.setAttribute('aria-expanded', 'false');
  }

  toggleDropdown(dropdown) {
    if (dropdown.classList.contains('open')) {
      this.closeDropdown(dropdown);
    } else {
      this.openDropdown(dropdown);
    }
  }

  setupSearchToggle() {
    const searchButton = document.querySelector('.search__button');
    const searchForm = document.querySelector('.search');

    if (!searchButton || !searchForm) return;

    if (searchButton.dataset.searchBound !== 'true') {
      searchButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSearch();
      });
      searchButton.dataset.searchBound = 'true';
    }

    // Chiudi search cliccando fuori
    if (!this.searchListenersBound) {
      document.addEventListener('click', this.handleSearchDocumentClick);
      document.addEventListener('keydown', this.handleSearchDocumentKeydown);
      this.searchListenersBound = true;
    }
  }

  toggleSearch() {
    const searchForm = document.querySelector('.search');
    const searchInput = document.querySelector('.search__input');

    if (searchForm.classList.contains('search--open')) {
      this.closeSearch();
    } else {
      searchForm.classList.add('search--open');
      if (searchInput) {
        setTimeout(() => searchInput.focus(), 100);
      }
    }
  }

  closeSearch() {
    const searchForm = document.querySelector('.search');
    if (searchForm) {
      searchForm.classList.remove('search--open');
    }
  }

  handleSearchDocumentClick(e) {
    const searchForm = document.querySelector('.search');
    if (!searchForm) return;

    if (!searchForm.contains(e.target) && searchForm.classList.contains('search--open')) {
      this.closeSearch();
    }
  }

  handleSearchDocumentKeydown(e) {
    const searchForm = document.querySelector('.search');
    if (!searchForm) return;

    if (e.key === 'Escape' && searchForm.classList.contains('search--open')) {
      this.closeSearch();
    }
  }

  setupKeyboardNavigation() {
    if (!this.header) return;

    const headerFocusableElements = this.header.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (headerFocusableElements.length === 0) return;

    // Trap focus nel mobile menu quando aperto
    if (this.mobileMenu && this.mobileMenu.dataset.keyboardBound === 'true') {
      return;
    }

    this.mobileMenu?.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && this.mobileMenu.open) {
        const mobileFocusableElements = this.mobileMenu.querySelectorAll(
          'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const firstElement = mobileFocusableElements[0];
        const lastElement = mobileFocusableElements[mobileFocusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    });

    if (this.mobileMenu) {
      this.mobileMenu.dataset.keyboardBound = 'true';
    }
  }

  // Public API
  refresh() {
    this.controller = document.querySelector('[data-header-controller]');
    this.header = this.controller ? this.controller.querySelector('.beezfy-header') : document.querySelector('.beezfy-header');
    this.announcement = this.controller ? this.controller.querySelector('[data-announcement-bar]') : document.querySelector('[data-announcement-bar]');
    this.dropdowns = this.controller ? this.controller.querySelectorAll('[data-dropdown]') : document.querySelectorAll('[data-dropdown]');
    this.mobileMenu = document.getElementById('mobile-menu-details');

    this.captureStructure();
    this.readConfig();
    this.setupMobileMenu();
    this.setupDropdowns();
    this.setupSearchToggle();
    this.setupKeyboardNavigation();
    this.setupResizeObserver();
    this.updateHeights();
    this.updateStickyState(window.scrollY, 0, true);
  }
}

// Inizializza quando DOM è pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.beezfyHeaderController = new BeezfyHeaderController();
  });
} else {
  window.beezfyHeaderController = new BeezfyHeaderController();
}

// Reinizializza su eventi Shopify
document.addEventListener('shopify:section:load', () => {
  if (window.beezfyHeaderController) {
    window.beezfyHeaderController.refresh();
  } else {
    window.beezfyHeaderController = new BeezfyHeaderController();
  }
});

document.addEventListener('shopify:section:reorder', () => {
  if (window.beezfyHeaderController) {
    window.beezfyHeaderController.refresh();
  }
});

// Export globale
window.BeezfyHeaderController = BeezfyHeaderController;
